import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ScrollText } from 'lucide-react'
import { Button } from '@/components/ui/button'

const actionLabels: Record<string, string> = {
  member_invited: 'Member Added',
  member_removed: 'Member Removed',
  member_role_changed: 'Role Changed',
  clinic_added: 'Clinic Added',
  clinic_removed: 'Clinic Removed',
  settings_updated: 'Settings Updated',
  org_updated: 'Organization Updated',
  data_exported: 'Data Exported',
  data_viewed: 'Data Viewed',
}

const OrgAuditLog: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>()
  const [page, setPage] = useState(1)

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['org-audit-log', orgId, page],
    queryFn: async () => {
      const res = await api.get(`/organizations/${orgId}/audit-log?page=${page}&page_size=50`)
      return res.data?.data
    },
    enabled: !!orgId,
  })

  const entries = auditData?.entries || []
  const pagination = auditData?.pagination || {}

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">
          Track all organization-level actions for compliance
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Activity Log</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center">
              <ScrollText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No audit entries yet</h3>
              <p className="text-sm text-slate-500 mt-1">
                Actions will be recorded here automatically
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="pb-3 font-medium">Timestamp</th>
                      <th className="pb-3 font-medium">Action</th>
                      <th className="pb-3 font-medium">User</th>
                      <th className="pb-3 font-medium">Target</th>
                      <th className="pb-3 font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry: Record<string, unknown>) => (
                      <tr key={entry.id as string} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-3 text-slate-500 whitespace-nowrap">
                          {new Date(entry.created_at as string).toLocaleString()}
                        </td>
                        <td className="py-3">
                          <Badge variant="outline">
                            {actionLabels[entry.action as string] || (entry.action as string)}
                          </Badge>
                        </td>
                        <td className="py-3">
                          {entry.user ? (
                            <span className="text-slate-900">
                              {(entry.user as Record<string, string>).first_name}{' '}
                              {(entry.user as Record<string, string>).last_name}
                            </span>
                          ) : (
                            <span className="text-slate-400">System</span>
                          )}
                        </td>
                        <td className="py-3 text-slate-500">
                          {entry.target_type ? `${entry.target_type}` : '—'}
                          {entry.target_id ? ` #${(entry.target_id as string).slice(0, 8)}` : ''}
                        </td>
                        <td className="py-3 text-slate-400 text-xs font-mono">
                          {(entry.ip_address as string) || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} entries)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pagination.total_pages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default OrgAuditLog
